// Import statements
import { ServiceProvider } from './services/serviceFactory';
import { UseCaseProvider } from './services/useCaseFactory';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { getPlatform } from './platform';

// Storage
export { ChromeStorageRepository } from './storage/chromeStorageRepository';
export { ChromePromptRepository } from './storage/chromePromptRepository';
export { ChromeFolderRepository } from './storage/chromeFolderRepository';
export { RepositoryFactory } from './storage/repositoryFactory';

// Storage Models
export type { StoragePrompt, StorageFolder } from './storage/models/storageTypes';

// Storage Mappers
export { PromptMapper } from './storage/mappers/promptMapper';
export { FolderMapper } from './storage/mappers/folderMapper';

// Events
export { ChromeEventPublisher } from './events/eventPublisher';

// Services
export { ServiceFactory, ServiceProvider } from './services/serviceFactory';
export { UseCaseFactory, UseCaseProvider } from './services/useCaseFactory';

// Builders and Factories
export { FolderHierarchyBuilder, SpecialFolderFactory } from './storage/repositoryFactory';

// Initialize Infrastructure
export function initializeInfrastructure(): void {
  // Initialize providers in the correct order
  const serviceProvider = getServiceProvider();
  const _useCaseProvider = getUseCaseProvider();

  // Initialize special folders
  const folderService = serviceProvider.getFolderService();
  folderService.initializeSpecialFolders().catch((error: Error) => {
    console.error('Failed to initialize special folders:', error);
  });

  // Tombstone system initialization removed to prevent folders from being randomly deleted
  //console.info('Tombstone system has been removed to prevent folders from being randomly deleted');

  // Subscribe to domain events for infrastructure concerns
  const eventPublisher = serviceProvider.getEventPublisher();

  // Example: Log all domain events
  const eventTypes = [
    'prompt.created',
    'prompt.updated',
    'prompt.deleted',
    'prompt.moved',
    'folder.created',
    'folder.updated',
    'folder.deleted',
    'folder.moved',
  ] as const;

  eventTypes.forEach(eventType => {
    eventPublisher.subscribe(eventType, async (event: { type: string; [key: string]: any }) => {
      console.log(`[Domain Event] ${event.type}:`, event);
    });
  });

  // Subscribe to deletion events - tombstone creation removed to prevent folders from being randomly deleted
  eventPublisher.subscribe('prompt.deleted', async event => {
    console.log('[Domain Event Handler] DIAGNOSTIC: prompt.deleted event received:', event);
    console.log('[Domain Event Handler] DIAGNOSTIC: event.payload:', event.payload);
    console.log(
      '[Domain Event Handler] DIAGNOSTIC: event.payload.promptId:',
      event.payload.promptId
    );

    // Tombstone creation has been removed to prevent folders from being randomly deleted
    console.log(
      '[Domain Event Handler] DIAGNOSTIC: Tombstone creation skipped to prevent random deletions'
    );
  });

  eventPublisher.subscribe('folder.deleted', async event => {
    console.log('[Domain Event Handler] DIAGNOSTIC: folder.deleted event received:', event);
    console.log('[Domain Event Handler] DIAGNOSTIC: event.payload:', event.payload);
    console.log(
      '[Domain Event Handler] DIAGNOSTIC: event.payload.folderId:',
      event.payload.folderId
    );

    // Tombstone creation has been removed to prevent folders from being randomly deleted
    console.log(
      '[Domain Event Handler] DIAGNOSTIC: Tombstone creation skipped to prevent random deletions'
    );
  });

  // Storage change listeners - tombstone creation removed to prevent folders from being randomly deleted
  // (the platform adapter already scopes events to the local storage area)
  getPlatform().storage.onChanged(changes => {
    // Check for prompt deletions
    if (changes[STORAGE_KEYS.PROMPTS]) {
      const oldValue = changes[STORAGE_KEYS.PROMPTS].oldValue || [];
      const newValue = changes[STORAGE_KEYS.PROMPTS].newValue || [];

      // Find deleted prompts by comparing old and new arrays
      const oldIds = new Set(oldValue.map((p: any) => p.id));
      const newIds = new Set(newValue.map((p: any) => p.id));

      // IDs that were in old but not in new were deleted
      const deletedIds = Array.from(oldIds).filter(id => !newIds.has(id));

      // Log deleted prompts but don't create tombstones
      for (const id of deletedIds as string[]) {
        console.info(
          `Detected prompt deletion from storage changes: ${id} (tombstone creation skipped)`
        );
      }
    }

    // Similar logic for folders
    if (changes[STORAGE_KEYS.FOLDERS]) {
      const oldValue = changes[STORAGE_KEYS.FOLDERS].oldValue || [];
      const newValue = changes[STORAGE_KEYS.FOLDERS].newValue || [];

      // Find deleted folders by comparing old and new arrays
      const oldIds = new Set(oldValue.map((f: any) => f.id));
      const newIds = new Set(newValue.map((f: any) => f.id));

      // IDs that were in old but not in new were deleted
      const deletedIds = Array.from(oldIds).filter(id => !newIds.has(id));

      // Log deleted folders but don't create tombstones
      for (const id of deletedIds as string[]) {
        console.info(
          `Detected folder deletion from storage changes: ${id} (tombstone creation skipped)`
        );
      }
    }
  });
}

// Export providers for easy access
export const getServiceProvider = ServiceProvider.getInstance;
export const getUseCaseProvider = UseCaseProvider.getInstance;

// Export commonly used services and use cases
export const getPromptService = () => getServiceProvider().getPromptService();
export const getFolderService = () => getServiceProvider().getFolderService();
export const getPromptManagementUseCase = () => getUseCaseProvider().getPromptManagementUseCase();
export const getFolderManagementUseCase = () => getUseCaseProvider().getFolderManagementUseCase();
